import { store } from 'redux/store';
import i18n from 'i18n/config';
import { useCallback, useEffect, useState } from 'react';

import { EmailIssue } from 'types';
import getInvalidEmails from 'utils/getInvalidEmails';
import getGeneratedEmails from 'utils/getGeneratedEmails';

import { getOperationParticipants } from 'modules/transactions/api/getOperationParticipants';
import { PartnerData } from '../types/PartnerData';

type UseGetPartnerDataProps = {
  operationId: string | undefined;
  page: number;
};

const getEmailAlert = (
  invalidEmailsData: EmailIssue[],
  invalidEmailsMessage: string,
  initialMessage: string
) => (
  <div className="text-left">
    <b>{invalidEmailsMessage}</b>
    <br />
    <p className="text-justify">
      {initialMessage} <br />
    </p>
    <ul>
      {invalidEmailsData.map((data) => (
        <li>• {data.name}</li>
      ))}
    </ul>
  </div>
);

const getInvalidEmailsAlert = (emails: EmailIssue[]) => {
  const invalidEmailsMessage = i18n.t('InvalidEmails');
  const initialMessage = i18n.t('PartnersWithInvalidEmailsMessage');

  const emailAlert = getEmailAlert(
    emails,
    invalidEmailsMessage,
    initialMessage
  );
  return emailAlert;
};

const getGeneratedEmailsAlert = (generatedEmailsData: EmailIssue[]) => {
  const invalidEmailsMessage = i18n.t('GeneratedEmails');
  const initialMessage = i18n.t('PartnersWithGeneratedEmailsMessage');

  const emailAlert = getEmailAlert(
    generatedEmailsData,
    invalidEmailsMessage,
    initialMessage
  );
  return emailAlert;
};

const fields = 'id name email';

export const useGetPartnersData = ({
  operationId,
  page,
}: UseGetPartnerDataProps) => {
  const [partnersData, setPartnersData] = useState<PartnerData[]>([]);
  const [totalPartners, setTotalPartners] = useState<number>(0);
  const [hasInvalidEmails, setHasInvalidEmails] = useState<boolean>(false);
  const [invalidEmailsAlert, setInvalidEmailsAlert] =
    useState<React.ReactNode>(); // eslint-disable-line

  const [hasGeneratedEmails, setHasGeneratedEmails] = useState<boolean>(false);
  const [generatedEmailsAlert, setGeneratedEmailsAlert] =
    useState<React.ReactNode>(); // eslint-disable-line

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getPartners = useCallback(async () => {
    if (!operationId) return;

    setIsLoading(true);

    try {
      const data = await store.dispatch(
        getOperationParticipants({
          operationId,
          params: {
            fields,
            page,
          },
        })
      );

      const { partners, total } = data;

      setPartnersData(partners);
      setTotalPartners(total);

      const invalidEmails = getInvalidEmails(partners);
      setHasInvalidEmails(invalidEmails.length > 0);

      const invalidEmailsAlert = getInvalidEmailsAlert(invalidEmails);
      setInvalidEmailsAlert(invalidEmailsAlert);

      const generateEmails = getGeneratedEmails(partners);
      setHasGeneratedEmails(generateEmails.length > 0);

      const generatedEmailsAlert = getGeneratedEmailsAlert(generateEmails);
      setGeneratedEmailsAlert(generatedEmailsAlert);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [operationId, page]);

  useEffect(() => {
    getPartners();
  }, [getPartners]);

  return {
    data: {
      partnersData,
      totalPartners,
      hasInvalidEmails,
      invalidEmailsAlert,
      hasGeneratedEmails,
      generatedEmailsAlert,
    },
    isLoading,
  };
};
